import { Alert, Badge, Checkbox, Link, Pagination, Row } from '@amzn/meridian';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { ErrorAlert } from 'components/ErrorAlert';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { uniq } from 'es-toolkit';
import type { JagListViewFragment } from 'generated/graphql';
import { type FC, useCallback } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom-v5-compat';
import { formattedUpdatedDate } from '../JagListView/jag-list-view.utils';
import { useJagListViewContext } from '../JagListViewContext';
import { EditJagButton } from './EditJagButton';
import { QaStatusBadge, type QaStatusBadgeEnum } from './QaStatusBadge';

export const JagListViewTable: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    allSelected,
    changePage,
    currentPageNumber,
    error,
    isJourneysAssetSystemQAEnabled,
    filteredJagsList,
    loading,
    noneSelected,
    numberOfPages,
    selectionState,
    updateSelectionState,
    toggleSelectAllPages,
    selectAllPages,
  } = useJagListViewContext();

  const onChangeRow = useCallback(
    (jagId: string) => (checked: boolean) => {
      const updatedSelectionState = {
        ...selectionState,
        [jagId]: checked,
      };
      updateSelectionState(updatedSelectionState);
    },
    [
      selectionState,
      updateSelectionState,
    ],
  );

  const onChangeHeader = useCallback(
    (checked: boolean) => {
      toggleSelectAllPages(checked);
    },
    [
      toggleSelectAllPages,
    ],
  );

  const getStatuses = useCallback(
    (jag: JagListViewFragment) =>
      jag.layeredImageAssets.nodes.map((lia) => lia.reviews.findLast((r) => r.status)?.status ?? 'NOT_REVIEWED'),
    [],
  );

  const getStatusCount = useCallback(
    (statuses: QaStatusBadgeEnum[]) =>
      statuses.reduce(
        (acc, status) => {
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        },
        {} as Record<QaStatusBadgeEnum, number>,
      ),
    [],
  );

  const getBadges = useCallback(
    (jag: JagListViewFragment) => {
      const statuses = getStatuses(jag) as QaStatusBadgeEnum[];
      const statusesWithCount = getStatusCount(statuses);
      return Object.entries(statusesWithCount);
    },
    [
      getStatusCount,
      getStatuses,
    ],
  );

  const getJagLocales = useCallback((jag: JagListViewFragment) => {
    const locales = jag.layeredImageAssets.nodes.map(({ locale, marketplace }) => {
      const correspondingMarketplace = jag.layeredImageAssets.marketplaces.find(({ id }) => marketplace.id === id);
      const correspondingLocale = correspondingMarketplace?.locales.edges.find(({ node }) => node.id === locale.id);
      return correspondingLocale?.displayName;
    });
    const uniqueLocales = uniq(locales);
    return uniqueLocales.map((locale) => <Row key={locale}>{locale}</Row>);
  }, []);

  return (
    <>
      <Table headerRows={1} showDividers spacing="small" stickyHeaderRow>
        {!loading && (
          <TableRow data-cy="jag-table-header-row">
            {isJourneysAssetSystemQAEnabled && (
              <TableCell>
                <Checkbox
                  checked={
                    selectAllPages ||
                    (filteredJagsList.length > 0 && (allSelected || noneSelected ? selectionState[filteredJagsList[0].id] : 'mixed'))
                  }
                  onChange={onChangeHeader}
                />
              </TableCell>
            )}
            <TableCell>Name</TableCell>
            <TableCell>Template Group</TableCell>
            <TableCell>Locales</TableCell>
            <TableCell>Last Updated At</TableCell>
            <TableCell>Created By</TableCell>
            {isJourneysAssetSystemQAEnabled && <TableCell>QA Status</TableCell>}
            <TableCell />
          </TableRow>
        )}
        {!loading ? (
          filteredJagsList.map((jag: JagListViewFragment) => (
            <TableRow data-cy={`${jag.id}-table-row`} key={jag.id}>
              {isJourneysAssetSystemQAEnabled && (
                <TableCell>
                  <Checkbox checked={selectAllPages || selectionState[jag.id]} onChange={onChangeRow(jag.id)} />
                </TableCell>
              )}
              <TableCell>
                <Link
                  href={generatePath(`${location.pathname}/:jagId`, {
                    jagId: jag.id,
                  })}
                  onClick={navigate}
                  type="secondary"
                >
                  {jag.name}
                </Link>
              </TableCell>
              <TableCell>{jag.templateGroup.name}</TableCell>
              <TableCell>{getJagLocales(jag)}</TableCell>
              <TableCell>{formattedUpdatedDate(jag.updatedAt!)}</TableCell>
              <TableCell>{jag.createdBy}</TableCell>
              {isJourneysAssetSystemQAEnabled && (
                <TableCell>
                  {getBadges(jag).map(([status, count]) => (
                    <div key={status}>
                      <QaStatusBadge status={status as QaStatusBadgeEnum} />
                      <Badge value={count} />
                    </div>
                  ))}
                </TableCell>
              )}
              <TableCell>
                <EditJagButton jag={jag} />
              </TableCell>
            </TableRow>
          ))
        ) : (
          <LoadingSpinner />
        )}
      </Table>
      {numberOfPages > 1 && (
        <Row alignmentHorizontal="center">
          <Pagination
            currentPage={currentPageNumber}
            data-cy="asset-group-pagination"
            numberOfPages={numberOfPages}
            onChange={changePage}
            showSkipArrows
          />
        </Row>
      )}
      {error && <ErrorAlert contextMessage="There was a problem loading asset groups" error={error} />}
      {!loading && filteredJagsList.length === 0 && !error && (
        <Alert size="large" type="theme">
          No matching asset groups found
        </Alert>
      )}
    </>
  );
};
